import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  PercentIcon,
  InfoIcon,
  Mail,
  AlertCircle,
} from "lucide-react";
import { FormattedNumberInput } from "../components/FormattedNumberInput";
import { useRefiBuddyState } from "../hooks/useRefiBuddyState";
import { useNationalRates } from "../hooks/useNationalRates";

interface NationalRate {
  id: string;
  date: string;
  source: string;
  rate: string;
  points: string | null;
  change: string | null;
  prior_year: string | null;
  yoy_change: string | null;
}

const LOAN_PROGRAMS = [
  { value: "30-year-fixed", label: "30 Year Fixed" },
  { value: "15-year-fixed", label: "15 Year Fixed" },
  { value: "5-1-arm", label: "5/1 ARM" },
];

const RefiBuddy: React.FC = () => {
  const navigate = useNavigate();
  const {
    refiBuddyData,
    currentView,
    setCurrentView,
    isLoading,
    setCurrentMortgage,
    setUserIntent,
  } = useRefiBuddyState();
  const { data: ratesData } = useNationalRates();
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const thirtyYearRate = ratesData?.rates.find((rate) =>
    rate.source.includes("MND's 30 Year Fixed")
  );

  const handleInputChange = (
    field: keyof typeof refiBuddyData.currentMortgage,
    value: string
  ) => {
    setCurrentMortgage({ [field]: value });
  };

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setUserIntent(refiBuddyData.intent || "get_notified", newEmail);
    if (emailError) {
      validateEmail(newEmail);
    }
  };

  const handleCheckRates = () => {
    setCurrentView("rate_comparison");
  };

  const handleRefinanceNow = () => {
    if (!validateEmail(refiBuddyData.email || "")) return;
    setIsSubmitting(true);
    setUserIntent("refinance_now", refiBuddyData.email || "");
    navigate("/loan-information");
  };

  const handleGetNotified = () => {
    if (!validateEmail(refiBuddyData.email || "")) return;
    setIsSubmitting(true);
    setUserIntent("get_notified", refiBuddyData.email || "");
    navigate("/loan-information");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const renderMortgageDetails = () => (
    <>
      <div className="bg-white rounded-xl shadow-sm p-2 sm:p-6 mb-8 max-w-3xl mx-auto">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">
          Current Mortgage Details
        </h2>

        {/* Interest Rate */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Current Interest Rate
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <PercentIcon size={18} className="text-gray-400" />
            </div>
            <input
              type="number"
              value={refiBuddyData.currentMortgage.rate}
              onChange={(e) => handleInputChange("rate", e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              placeholder="e.g., 5.25"
              className="input-field pl-10 w-full [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [appearance:textfield]"
            />
          </div>
        </div>

        {/* Loan Program */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Loan Program
          </label>
          <select
            value={refiBuddyData.currentMortgage.loanProgram}
            onChange={(e) => handleInputChange("loanProgram", e.target.value)}
            className={`input-field w-full ${
              refiBuddyData.currentMortgage.loanProgram === ""
                ? "text-gray-400"
                : "text-gray-900"
            }`}
          >
            <option value="" disabled>
              Select a loan program
            </option>
            {LOAN_PROGRAMS.map((program) => (
              <option key={program.value} value={program.value}>
                {program.label}
              </option>
            ))}
          </select>
        </div>

        {/* Monthly Payment */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monthly Payment
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign size={18} className="text-gray-400" />
            </div>
            <FormattedNumberInput
              value={
                refiBuddyData.currentMortgage.monthlyPayment
                  ? parseFloat(refiBuddyData.currentMortgage.monthlyPayment)
                  : NaN
              }
              onChange={(num) =>
                handleInputChange("monthlyPayment", num.toString())
              }
              placeholder="e.g., 2,500"
              prefix="dollar"
              min={0}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Enter your monthly payment including principal and interest (P&I)
          </p>
        </div>

        {/* Mortgage Insurance */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Monthly Mortgage Insurance (PMI)
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <DollarSign size={18} className="text-gray-400" />
            </div>
            <FormattedNumberInput
              value={
                refiBuddyData.currentMortgage.mortgageInsurance
                  ? parseFloat(refiBuddyData.currentMortgage.mortgageInsurance)
                  : NaN
              }
              onChange={(num) =>
                handleInputChange("mortgageInsurance", num.toString())
              }
              placeholder="e.g., 175"
              prefix="dollar"
              min={0}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Enter your monthly mortgage insurance payment if applicable
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button onClick={() => navigate(-1)} className="btn-secondary">
            Back
          </button>
          <button
            onClick={handleCheckRates}
            disabled={
              !refiBuddyData.currentMortgage.rate ||
              !refiBuddyData.currentMortgage.monthlyPayment ||
              !refiBuddyData.currentMortgage.loanProgram
            }
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Check Rates
          </button>
        </div>
      </div>
    </>
  );

  const renderRateComparison = () => {
    const currentRate = parseFloat(refiBuddyData.currentMortgage.rate);
    const nationalRate = thirtyYearRate
      ? parseFloat(thirtyYearRate.rate)
      : null;
    const rateDifference = nationalRate ? currentRate - nationalRate : 0;
    const isGoodTimeToRefi = rateDifference > 0.0;

    return (
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8 max-w-3xl mx-auto">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">
          {isGoodTimeToRefi
            ? "Check Rates Now"
            : "Get Notified When Rates Improve"}
        </h2>

        {nationalRate && (
          <div
            className={`p-4 rounded-lg mb-8 ${
              isGoodTimeToRefi
                ? "bg-green-50 text-green-700"
                : "bg-yellow-50 text-yellow-700"
            }`}
          >
            <div className="flex items-start">
              <AlertCircle size={20} className="shrink-0 mr-3 mt-0.5" />
              <div>
                <p className="font-medium mb-2">
                  {isGoodTimeToRefi
                    ? "Now may be a good time to refinance!"
                    : "Now may not be the best time to refinance"}
                </p>
                {!isGoodTimeToRefi && (
                  <p className="text-sm">
                    The current national average is {nationalRate}%.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Email Input */}
          <div>
            <p className="text-gray-600 mb-4">
              Get a quote estimate today, and we'll email you as rates change.
            </p>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email Address *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={18} className="text-gray-400" />
              </div>
              <input
                type="email"
                id="email"
                value={refiBuddyData.email || ""}
                onChange={handleEmailChange}
                placeholder="Enter your email"
                className={`input-field pl-10 w-full ${
                  emailError ? "border-error ring-1 ring-error" : ""
                }`}
              />
            </div>
            {emailError && (
              <p className="mt-1 text-sm text-error">{emailError}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center">
            <div className="space-y-4 sm:w-1/2 flex flex-col gap-2">
              <button
                onClick={handleGetNotified}
                disabled={isSubmitting}
                className="btn-primary w-full rounded-full py-3"
              >
                {isSubmitting ? "Submitting..." : "Continue to Loan Scenario"}
              </button>

              <div className="text-center text-sm">
                <button
                  onClick={handleRefinanceNow}
                  disabled={isSubmitting}
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  Check Rates Now
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={() => setCurrentView("mortgage_details")}
            className="text-primary hover:text-primary-dark font-medium"
          >
            ← Back to Mortgage Details
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="py-6 sm:py-12 px-4">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto text-center mb-6 sm:mb-12">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
            Never Miss a Chance to Refinance
          </h1>
          <p className="text-xl text-gray-600 sm:block hidden">
            Tell us about your current mortgage, and we'll notify you when
            refinancing could save you money.
          </p>
        </div>

        {/* Info Section */}
        <div className="bg-primary-light/5 border border-primary-light/20 rounded-lg p-6 mb-6 sm:mb-10 max-w-3xl mx-auto">
          <div className="flex">
            <InfoIcon size={20} className="text-primary shrink-0 mr-3 mt-1" />
            <div>
              <h3 className="font-medium text-gray-900 mb-2">
                How Refi Buddy Works:
              </h3>
              <ol className="list-decimal list-inside space-y-4 text-gray-700">
                <li>
                  <span className="font-medium">
                    Share Your Current Mortgage Details
                  </span>
                  <p className="ml-6 mt-1">
                    Enter your current interest rate and other basic details
                    about your mortgage.
                  </p>
                </li>
                <li>
                  <span className="font-medium">We Monitor Market Rates</span>
                  <p className="ml-6 mt-1">
                    Our system automatically checks current market rates and
                    compares them to your mortgage.
                  </p>
                </li>
                <li>
                  <span className="font-medium">
                    Get Notified When It Makes Sense
                  </span>
                  <p className="ml-6 mt-1">
                    We'll email you when refinancing could save you money,
                    including a detailed analysis of potential savings.
                  </p>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {currentView === "mortgage_details"
          ? renderMortgageDetails()
          : renderRateComparison()}
      </div>
    </div>
  );
};

export default RefiBuddy;
